import React from 'react';

// ─── Shared Skeleton Atom ────────────────────────────────────────────────────
// Each bar pulses with a shimmer — matches the app's card style exactly.
const S = ({
    w = 'w-full',
    h = 'h-4',
    r = 'rounded-lg',
    cls = '',
}: {
    w?: string;
    h?: string;
    r?: string;
    cls?: string;
}) => (
    <div
        className={`${w} ${h} ${r} ${cls} flex-shrink-0`}
        style={{
            background: 'linear-gradient(90deg,var(--v2-border-subtle) 25%,var(--v2-border-card) 50%,var(--v2-border-subtle) 75%)',
            backgroundSize: '200% 100%',
            animation: 'skeletonShimmer 1.6s ease-in-out infinite',
        }}
    />
);

// ─── LabDashboard Skeleton ────────────────────────────────────────────────────
// Mirrors the new premium unified header card + 5 KPI cards + revenue/summary row + orders table
const LabDashboardSkeleton: React.FC = () => (
    <div className="flex flex-col gap-4 v2-animate-fade-in">

        {/* Premium header card */}
        <div className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] overflow-hidden" style={{ boxShadow: 'var(--v2-shadow-card)' }}>
            <div className="h-[3px]" style={{ background: 'linear-gradient(90deg,#059669 0%,#34d399 55%,#7C3AED 100%)' }} />
            <div className="px-5 pt-4 pb-0">
                {/* Row 1: identity + actions */}
                <div className="flex items-start justify-between gap-4">
                    <div className="flex flex-col gap-2.5">
                        <div className="flex items-center gap-2">
                            <S w="w-7" h="h-7" r="rounded-xl" />
                            <S w="w-36" h="h-5" r="rounded-lg" />
                        </div>
                        {/* Live stat pills */}
                        <div className="flex items-center gap-6">
                            {[72, 88, 48, 96].map((w, i) => (
                                <S key={i} w={`w-[${w}px]`} h="h-3" r="rounded-md" />
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <S w="w-20" h="h-7" r="rounded-xl" />
                        <S w="w-24" h="h-7" r="rounded-xl" />
                    </div>
                </div>
                {/* Tab row */}
                <div className="flex items-center gap-0 mt-3">
                    <S w="w-20" h="h-9" r="rounded-t-lg" cls="!rounded-b-none" />
                    <S w="w-32" h="h-9" r="rounded-t-lg" cls="!rounded-b-none ml-1 opacity-50" />
                </div>
            </div>
        </div>

        {/* 5 KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] p-5 flex flex-col gap-4 overflow-hidden relative">
                    <div className="absolute inset-x-0 top-0 h-[3px] rounded-t-2xl" style={{ background: 'var(--v2-border-subtle)' }} />
                    <div className="flex items-start justify-between">
                        <S w="w-24" h="h-2.5" r="rounded-md" />
                        <S w="w-10" h="h-10" r="rounded-2xl" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <S w="w-28" h="h-8" r="rounded-lg" />
                        <S w="w-32" h="h-2.5" r="rounded-md" />
                    </div>
                </div>
            ))}
        </div>

        {/* Revenue + Summary row */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-3">
            <div className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <S w="w-36" h="h-3" r="rounded-md" />
                    <div className="flex gap-3">
                        <S w="w-14" h="h-3" r="rounded-md" />
                        <S w="w-18" h="h-3" r="rounded-md" />
                    </div>
                </div>
                {[0, 1].map(i => (
                    <div key={i} className="flex flex-col gap-1.5">
                        <div className="flex items-center justify-between">
                            <S w="w-28" h="h-2.5" r="rounded-md" />
                            <S w="w-16" h="h-2.5" r="rounded-md" />
                        </div>
                        <S w="w-full" h="h-2" r="rounded-full" />
                    </div>
                ))}
                <div className="h-px bg-[var(--v2-border-subtle)]" />
                {[0, 1].map(i => (
                    <div key={i} className="flex items-center justify-between">
                        <S w="w-28" h="h-2.5" r="rounded-md" />
                        <S w="w-6" h="h-4" r="rounded-md" />
                    </div>
                ))}
            </div>
            <div className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] p-4 flex flex-col gap-3">
                <S w="w-32" h="h-3" r="rounded-md" />
                {[0, 1, 2, 3].map(i => (
                    <div key={i} className="flex items-center justify-between">
                        <S w="w-24" h="h-2.5" r="rounded-md" />
                        <S w="w-16" h="h-2.5" r="rounded-md" />
                    </div>
                ))}
                <div className="h-px bg-[var(--v2-border-subtle)]" />
                <S w="w-full" h="h-8" r="rounded-xl" />
            </div>
        </div>

        {/* Recent Orders table */}
        <div className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--v2-border-subtle)] flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <S w="w-5" h="h-5" r="rounded-lg" />
                    <S w="w-28" h="h-3" r="rounded-md" />
                    <S w="w-6" h="h-4" r="rounded-full" />
                </div>
                <S w="w-16" h="h-6" r="rounded-lg" />
            </div>
            <div className="flex gap-6 px-4 py-2 border-b border-[var(--v2-border-subtle)]">
                {[140, 140, 80, 80, 80, 80, 80].map((w, i) => (
                    <S key={i} w={`w-[${w}px]`} h="h-2.5" r="rounded-md" />
                ))}
            </div>
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-6 px-4 py-2.5 border-b border-[var(--v2-border-divider)] last:border-0">
                    <div className="flex items-center gap-2 w-[140px] flex-shrink-0">
                        <S w="w-7" h="h-7" r="rounded-lg" />
                        <div className="flex flex-col gap-1 flex-1">
                            <S w="w-24" h="h-2.5" r="rounded-md" />
                            <S w="w-16" h="h-2" r="rounded-md" />
                        </div>
                    </div>
                    <S w="w-[140px]" h="h-2.5" r="rounded-md" />
                    <S w="w-[80px]" h="h-2.5" r="rounded-md" />
                    <S w="w-[80px]" h="h-5" r="rounded-full" />
                    <S w="w-[80px]" h="h-2.5" r="rounded-md" />
                    <S w="w-[80px]" h="h-2.5" r="rounded-md" />
                    <S w="w-[80px]" h="h-5" r="rounded-full" />
                </div>
            ))}
        </div>
    </div>
);

export default LabDashboardSkeleton;
